// ---------------------------------------------------------------------------
// سفرة تاتا وصال in three.js. The tray shows a food and its name is spoken;
// only the letters of that name float in the sky. Tap the next one and it names
// itself and flies into its slot; tap any other and it still names itself and
// shakes it off — there is no fail state, and no tap goes unanswered.
//
// A finished word is Teta's cue, and the camera is on her and her table for it.
// The dish appears in her hands, she turns to face the table, says what she has
// made, and it leaves her hands in an arc and lands ON it. Her grandson, who
// has been off wandering the ring, looks over, breaks into a run, comes into
// the picture, eats it at the table's edge and says what it was like — and
// then bolts back out to the edge, so the next one never lands at his feet.
// The dish stays on the table; eight of them, and then the oldest goes to
// make room. Then the next word is dealt.
//
// The spelling half of all that is the engine's and is shared with Jiddo's
// garden (core/), and so are the frame loop and the resize. What is here is
// hers: her place, her dishes, the three people, and the boot. Whose sofra it
// is — what she serves, what everyone says, in which language, and who the
// three of them are — is the pack (pack.ts), handed to play() by an entry
// point: main.ts for Danny's, f/<slug>/main.ts for another family's.
// ---------------------------------------------------------------------------
import {setVoice} from '../../core/audio';
import {setScript,setVowelled} from '../../core/letters';
import {setPlay} from '../../core/harness';
import {exposeDebug,startLoop} from '../../core/loop';
import {round,setRound,spawn} from '../../core/round';
import {T,follow,placeCam,view} from '../../core/scene';
import {openTrace,setTrace,traceClips,traceDebug,updateTrace} from '../../core/trace';
import {setCount,setTray} from '../../core/tray';
import {loadDishes,ready} from './dishes';
import {dishes,launchDish,serve,tata,updateField} from './field';
import {aim,hands,loadTata,make,tataDebug,updateTata} from './tata';
import {boy,boyDebug,loadBoy,updateBoy} from './boy';
import {baba,babaDebug,babaSaw,loadBaba,updateBaba} from './baba';
import {setPack,type TataPack} from './pack';
import {SOFRA,loadTable} from './set';           // her table, her trees, her house

export function play(p:TataPack){
  setPack(p);
  if(p.SCRIPT)setScript(p.SCRIPT);              // Arabic otherwise (core/letters.ts)
  setVowelled(p.DIA);                            // the حركات the tray, the sky and the board show (Arabic's alone)
  const {DIA,FOODS,INTRO,MAKE,NAMES,SAHTEIN,YUM,MAKE_ANY,YUM_ANY}=p;
  // How a word is made. 'spell' is every letter of it tapped out of the sky in
  // order (core/round.ts); 'trace' is its first letter drawn over with a
  // finger, the consonant and then its vowel sign, before the whole word comes
  // together (core/trace.ts). The pack says which its child gets (MODE) and the
  // address can say otherwise — ?mode=spell, or ?spell and ?trace for short —
  // so either is always one link away. These are the first two of what are
  // meant to become levels of difficulty, and a new one is a name here.
  const MODES=['spell','trace'] as const;
  type Mode=typeof MODES[number];
  const q=new URLSearchParams(location.search), asked=q.get('mode')??MODES.find(m=>q.has(m));
  const MODE:Mode=MODES.find(m=>m===asked)??p.MODE??'spell';
  const TRACE=MODE==='trace';
  if(TRACE)setTrace({own:k=>k in NAMES,borrow:p.SYLLABLES??'../vowels/audio/'});

  // What there is to say, and the order to ask for it in. Her hello first: the
  // first round's letters wait on it.
  const want:[string,string,string?][]=[['R',INTRO]];
  for(const k of Object.keys(NAMES))want.push(['L',k]);
  if(TRACE)want.push(...traceClips(FOODS.map(f=>f.w)));   // the board's syllables that are not letters of these words
  for(const f of FOODS)want.push(['W',f.w]);
  for(const c of [...MAKE_ANY,...YUM_ANY])want.push(['R',c]);
  for(const f of FOODS){want.push(['R',MAKE(f.w)]);want.push(['R',YUM(f.w)]);}   // a 404 here is fine
  want.push(['R',SAHTEIN]);                        // بابا's one line, last: nothing waits on it
  setVoice({say:NAMES,dia:DIA,want,dir:p.AUDIO});   // audio/ beside the page unless the pack says where
  // The picture IS the question the round asks, and hers are not drawn yet.
  // jiddo/ has a painted picture for every animal on his roster;
  // a rendered thumbnail of each dish is the obvious way to earn the same here,
  // except that scripts/render-art.py goes through clipsheet.html, which builds
  // one row PER CLIP and so draws nothing at all for a model with no animations.
  // Until that is fixed, the food's own emoji — set as an SVG data URL, the same
  // trick every page in this house uses for its favicon — is a clearer question
  // for a three-year-old than a placeholder, and costs nothing. A real picture
  // replaces it by changing this one line.
  const card=(w:string)=>{
    const e=FOODS.find(f=>f.w===w)?.e||'🍽️';
    return 'data:image/svg+xml,'+encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">'+e+'</text></svg>');
  };
  setTray({pic:card,slots:!TRACE});              // on the board, the word is only shown once it is whole

  setRound({
    roster:FOODS,
    ready,
    intro:INTRO,
    greet:()=>{},                                  // her hello is a line, not a gesture: she has no wave clip
    // The word is done. The dish appears in her hands and she turns to face the
    // table; the poof and the badge land on that, and it leaves her hands a beat
    // later, once she has come round to it.
    arrive:(w,t)=>{
      const spec=FOODS.find(f=>f.w===w)!;
      const {dish,to}=serve(spec,hands,boy.position,t);
      aim(to,t);
      launchDish(dish);
      return to;
    },
    // Then she names what she has made, and the round waits for her to finish —
    // and for the dish to come down, whichever is longer.
    answer:(to,t)=>make(to as {x:number;z:number},round.target!.w,t),
    // The badge counts what he has EATEN, not what is on the table. The table
    // holds ten and then the oldest goes, so what is standing about tops out;
    // how many she has fed him never does, and it is the thing worth showing a
    // child.
    count:()=>eaten,
    elder:tata,
    poke:()=>{},                                   // nothing recorded for a tap on her yet
    pickables:()=>dishes,
    deal:TRACE?openTrace:undefined,
  });

  let eaten=0;
  // The boy has a dish in his hands. The badge moves, and his father is told —
  // which only brings his urge to speak forward (babaSaw, baba.ts); the quiet
  // after the boy's own يمّ is what actually decides when he says it.
  const ate=(t:number)=>{eaten++;setCount(eaten);babaSaw(t);};   // the dish itself stays on the table (boy.ts marks it eaten)
  const EYE=0.3;                                   // how far from the table toward her the camera pivots

  // Her part of the ?dbg handle (exposeDebug, core/loop.ts): the table and what
  // is on it, a dish straight into the field, and the three of them. The yaw it
  // can set is how both grown-ups are checked at all — they place themselves
  // against the camera, her orbit by angle (tata.ts) and his father by what
  // the lens can actually see (baba.ts) — and 72 angles is what hers was
  // measured over.
  exposeDebug({get dishes(){return dishes},get eaten(){return eaten},get tpl(){return ready},foods:FOODS.map(f=>f.w),
    get boy(){return boy},get boyState(){return boyDebug.state},
    get boyTarget(){return boyDebug.target},get boyClip(){return boyDebug.clip},get boyCarry(){return boyDebug.carrying},
    get boyRig(){return {strides:boyDebug.strides,lifts:boyDebug.lifts}},
    get baba(){return baba},get babaState(){return babaDebug.state},get babaClip(){return babaDebug.clip},
    get babaTarget(){return babaDebug.target},get babaModel(){return babaDebug.model},
    get babaSaid(){return babaDebug.said},get babaSayAt(){return babaDebug.sayAt},get babaStretchUntil(){return babaDebug.stretchUntil},
    get babaRig(){return {stride:babaDebug.stride,lifts:babaDebug.lifts}},
    get tata(){return tata},get tataClip(){return tataDebug.clip},
    get tataState(){return tataDebug.state},get tataModel(){return tataDebug.model},get boyModel(){return boyDebug.model},
    table:SOFRA,trace:traceDebug,
    // With no word, the widest dish on the roster: the one that tests the table's room.
    spawn:(w?:string)=>spawn(w||FOODS.reduce((a,b)=>b.across>a.across?b:a).w,performance.now())});
  setPlay(traceDebug.play);                      // with no chips up, play() draws the board's next layer

  // ---- boot ----
  loadTable(); loadTata(); loadBoy(); loadBaba(); loadDishes();
  boy.position.set(4.2,0,-3.0);                    // out on his ring, where the game opens with all three of them in shot
  // And his father out on his own ring, on the other side of the sofra from his
  // mother, so the four of them (the two grown-ups, the boy and the table) open
  // the game spread across the picture rather than stacked on one edge of it. His
  // own ring keeps him out there (baba.ts).
  //
  // At the same DEPTH as the boy rather than a stride in front of him: at z -1.3
  // he stood between the lens and his son and hid him for the whole of the
  // opening, and the boy is the one a child is meant to find first.
  baba.position.set(2.0,0,-3.2);
  // What the camera pivots about, vertically. The engine's 1.5 is a grown-up's
  // waist, which is right for Jiddo — his animals stand on the grass and he is
  // what you are looking at. Hers is a LOW table, its top at 0.72, and at 1.5
  // the ground around the pivot sat 68% of the way down the screen, which on a
  // short wide phone is behind the tray: the sofra and the food she had just
  // thrown onto it were both hidden. Lowering the pivot lifts the whole floor
  // into the picture and still leaves her head clear of the letters; and the
  // table top sits under it, so it is seen from above rather than edge-on.
  T.y=1.05;
  T.set(SOFRA.x+(tata.position.x-SOFRA.x)*EYE,T.y,SOFRA.z+(tata.position.z-SOFRA.z)*EYE);
  startLoop((t,dt)=>{
    // The camera holds HER and her table, pivoting near the table and leaning a
    // little her way. It followed him for a while, which meant she walked out of
    // shot and the food arrived from nowhere at his feet. On her alone, the table
    // swung to the bottom of the frame whenever she stood behind it — and since
    // her orbit deliberately keeps her on the far side, that was most of the
    // time, with the food landing under the tray. Pivoting at the table is what
    // lifts it clear: everything beyond the pivot rides UP the frame, and she and
    // the boy are both always beyond it. She still never leaves the middle of the
    // picture — her whole orbit is within a stride and a half of the table —
    // and he is the one who comes into it, which is the whole beat.
    follow(SOFRA.x+(tata.position.x-SOFRA.x)*EYE,SOFRA.z+(tata.position.z-SOFRA.z)*EYE,dt);
    placeCam(view.yaw,view.el);
    updateTata(t,dt);
    updateBoy(t,dt,()=>ate(t));
    updateBaba(t,dt);                              // after both of them: he is the one who yields
    updateField(t);
    if(TRACE)updateTrace(t);
  },()=>dishes.length+' 🍽');
}
